import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ChatWorkspace } from '@/features/chat/components/workspace/ChatWorkspace';
import { requirePermission } from '@/lib/guards';

const tabSchema = z.enum(['active', 'closed']).default('active');

/**
 * Search-param model for the chat workspace (mirrors the sessions workspace).
 *   tab: Active | Closed rail + layout mode (2-pane vs 3-pane).
 *   studentId: Closed tab only, the student whose history the middle pane shows.
 *   chatId: the conversation open in the right pane.
 *   unassigned: Closed tab only, narrows to chats with no concerns recorded
 *     (the "Not assigned" filter) across both the rail and the history pane.
 *
 * The transform keeps the Active tab a clean two-pane view: it has no student
 * history middle pane, so a stray studentId / unassigned flag is stripped there.
 */
const chatWorkspaceSearchSchema = z
  .object({
    tab: tabSchema,
    studentId: z.string().optional(),
    chatId: z.string().optional(),
    unassigned: z.boolean().optional(),
  })
  .transform((val) =>
    val.tab === 'active' ? { ...val, studentId: undefined, unassigned: undefined } : val,
  );

export type ChatWorkspaceSearch = z.infer<typeof chatWorkspaceSearchSchema>;

export const Route = createFileRoute('/app/chat/')({
  beforeLoad: () => {
    // Psychologists and chief psychologists only; volunteers and customer
    // support never hold chat:respond.
    requirePermission('chat:respond');
  },
  validateSearch: (raw: Record<string, unknown>) => chatWorkspaceSearchSchema.parse(raw),
  component: ChatWorkspace,
  staticData: { crumb: 'Chat inbox', fullBleed: true },
});
